import { useState } from "react";
import { EyeOff, LogOut } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";

/**
 * Persistent banner shown while a SuperAdmin is viewing the platform as another user.
 *
 * Deliberately loud and impossible to dismiss. During an impersonation the UI is otherwise
 * indistinguishable from the target's own account — same roles, same navigation, same data — so
 * without a constant marker an admin can genuinely forget which account they are acting in and take a
 * real action believing it is their own. There is no close button for the same reason: the only way to
 * remove it is to actually end the session.
 *
 * Rendered at the app root so it survives navigation, and gated on `isImpersonating` rather than on an
 * admin role — during an impersonation the token carries the TARGET's roles, so an admin-role check
 * here would hide the banner in exactly the state it exists for.
 */
export function ImpersonationBanner() {
  const { isImpersonating, impersonatedUserName, user, stopImpersonation } = useAuth();
  const [exiting, setExiting] = useState(false);

  if (!isImpersonating) return null;

  // Prefer the dedicated claim: it carries the target's real full name, whereas `username` holds
  // UserName, which in this system is their login email.
  const displayName = impersonatedUserName || user?.username || user?.email || "this user";

  const handleExit = async () => {
    setExiting(true);
    const error = await stopImpersonation();
    if (error) {
      toast.error(error);
      setExiting(false);
      return;
    }
    toast.success("Impersonation ended.");
    // Full reload so every cached query is refetched under the restored admin identity. A soft state
    // update would leave React Query serving the impersonated user's data to the admin.
    window.location.replace("/");
  };

  return (
    <div
      // role=alert so assistive tech announces the changed identity, not just sighted users.
      role="alert"
      className="sticky top-0 z-[100] flex flex-wrap items-center justify-center gap-x-3 gap-y-2 border-b border-amber-600/40 bg-amber-500 px-4 py-2 text-sm font-medium text-amber-950 shadow-md dark:bg-amber-600 dark:text-amber-50"
    >
      <span className="flex items-center gap-2">
        <EyeOff className="size-4 shrink-0" aria-hidden="true" />
        <span>
          You are viewing as <strong className="font-semibold">{displayName}</strong>
        </span>
      </span>

      <Button
        size="sm"
        variant="outline"
        onClick={handleExit}
        disabled={exiting}
        className="h-7 border-amber-950/30 bg-amber-50/90 text-amber-950 hover:bg-amber-50 dark:border-amber-50/30 dark:bg-amber-950/40 dark:text-amber-50 dark:hover:bg-amber-950/60"
      >
        <LogOut className="size-3.5" aria-hidden="true" />
        {exiting ? "Exiting…" : "Exit Impersonation"}
      </Button>
    </div>
  );
}
